import { useCallback, useRef, useState } from 'react';
import { useJobPolling } from 'hooks/useJobPolling';

import { MAX_UPLOAD_FILES } from 'Pages/TeamDashboard/CreateReview/components/constants';

import FilesService from 'services/files';
import JobsService from 'services/jobs';

import { errorReactToastify } from 'utlis/toasts';

const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const MAX_VIDEO_SIZE_MB = 100;

export const VIDEO_PHASES = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  PROCESSING: 'processing',
  FAILED: 'failed',
};

const useScreenUpload = ({ uploadedImages, setUploadedImages, setOriginalFiles }) => {
  const fileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);
  const videoCancelledRef = useRef(false);
  const uidToFileRef = useRef({});

  const [videoPhase, setVideoPhase] = useState(VIDEO_PHASES.IDLE);
  const [videoJobId, setVideoJobId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleVideoJobCompleted = useCallback(outputPayload => {
    const mappedFrames = (outputPayload?.frames || []).map((frame, index) => ({
      name: `Frame ${index + 1}`,
      uid: `video-frame-${Date.now()}-${index}`,
      preview: `${process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL}${frame.s3_key}`,
      url: `${process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL}${frame.s3_key}`,
      progress: 100,
      isProgressLoading: false,
      isVideoFrame: true,
      timestamp: frame.timestamp_ms,
    }));

    setUploadedImages(mappedFrames);
    setVideoPhase(VIDEO_PHASES.IDLE);
    setVideoJobId(null);
  }, [setUploadedImages]);

  const handleVideoJobFailed = useCallback(error => {
    setVideoPhase(VIDEO_PHASES.FAILED);
    errorReactToastify(
      error || 'Video processing failed. Please try again.',
      {
        autoClose: 5000,
        position: 'bottom-center',
        hideProgressBar: true,
        style: { backgroundColor: '#FDF0F0', width: '470px' },
      }
    );
  }, []);

  const { stopPolling: stopVideoPolling } = useJobPolling({
    jobId: videoJobId,
    interval: 3000,
    onCompleted: handleVideoJobCompleted,
    onFailed: handleVideoJobFailed,
  });

  function processFiles(files) {
    if (uploadedImages.length + files.length > MAX_UPLOAD_FILES) {
      errorReactToastify(`You can only upload up to ${MAX_UPLOAD_FILES} images`, {
        autoClose: 5000,
        position: 'bottom-center',
        hideProgressBar: true,
        style: { backgroundColor: '#FDF0F0', width: '470px' },
      });

      return;
    }

    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      errorReactToastify('Please upload valid image files', {
        autoClose: 5000,
        position: 'bottom-center',
        hideProgressBar: true,
        style: { backgroundColor: '#FDF0F0', width: '470px' },
      });

      return;
    }

    const filesWithId = imageFiles.map(file => {
      const uid = Date.now() + Math.random();

      uidToFileRef.current[uid] = file;

      return {
        ...file,
        name: file.name,
        uid,
        // eslint-disable-next-line compat/compat
        preview: URL.createObjectURL(file),
        progress: 0,
        isProgressLoading: false,
        url: null,
      };
    });

    setUploadedImages(prev => [...prev, ...filesWithId]);

    if (setOriginalFiles) {
      setOriginalFiles(Object.values(uidToFileRef.current));
    }
  }

  async function handleVideoUpload(file) {
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      errorReactToastify('Please select a valid video file (MP4, MOV, or WEBM).');

      return;
    }

    if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
      errorReactToastify(`Video must be smaller than ${MAX_VIDEO_SIZE_MB}MB.`);

      return;
    }

    videoCancelledRef.current = false;
    setVideoPhase(VIDEO_PHASES.UPLOADING);

    try {
      const videoUrl = await FilesService.uploadFileFromLocal(file);

      if (videoCancelledRef.current) {
        return;
      }

      const jobResponse = await JobsService.createJob('video_frame_extraction', { video_url: videoUrl });

      if (videoCancelledRef.current) {
        return;
      }

      const newJobId = jobResponse.uuid || jobResponse.job_id;

      setVideoPhase(VIDEO_PHASES.PROCESSING);
      setVideoJobId(newJobId);
    }
    catch (_err) {
      if (!videoCancelledRef.current) {
        setVideoPhase(VIDEO_PHASES.FAILED);
        errorReactToastify('Failed to upload video. Please try again.');
      }
    }
  }

  function handleFileSelect(event) {
    const files = Array.from(event.target.files);

    processFiles(files);
    event.target.value = '';
  }

  function handleVideoFileSelect(event) {
    const file = event.target.files?.[0];

    if (file) {
      handleVideoUpload(file);
    }

    event.target.value = '';
  }

  function triggerFileInput() {
    fileInputRef.current?.click();
  }

  function triggerVideoFileInput() {
    videoFileInputRef.current?.click();
  }

  function handleVideoRetry() {
    setVideoPhase(VIDEO_PHASES.IDLE);
    setVideoJobId(null);
  }

  function removeImage(uid) {
    delete uidToFileRef.current[uid];
    setUploadedImages(prev => prev.filter(img => img.uid !== uid));

    if (setOriginalFiles) {
      setOriginalFiles(Object.values(uidToFileRef.current));
    }
  }

  function clearAll() {
    uidToFileRef.current = {};
    setUploadedImages([]);

    if (setOriginalFiles) {
      setOriginalFiles([]);
    }

    videoCancelledRef.current = true;
    stopVideoPolling();
    setVideoPhase(VIDEO_PHASES.IDLE);
    setVideoJobId(null);
  }

  // Drag and drop handlers
  function handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();

    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);

    if (files.length === 0) {
      return;
    }

    const droppedVideos = files.filter(file => ACCEPTED_VIDEO_TYPES.includes(file.type));
    const droppedImages = files.filter(file => file.type.startsWith('image/'));

    if (droppedVideos.length > 0 && droppedImages.length > 0) {
      errorReactToastify('Please drop either video files or image files, not both.');

      return;
    }

    if (droppedVideos.length > 1) {
      errorReactToastify('Please drop only one video file at a time.');

      return;
    }

    if (droppedVideos.length === 1) {
      handleVideoUpload(droppedVideos[0]);

      return;
    }

    processFiles(files);
  }

  return {
    fileInputRef,
    videoFileInputRef,
    uidToFileRef,
    videoPhase,
    isDragging,
    triggerFileInput,
    triggerVideoFileInput,
    handleFileSelect,
    handleVideoFileSelect,
    handleVideoRetry,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    removeImage,
    clearAll,
    isVideoActive: videoPhase !== VIDEO_PHASES.IDLE,
  };
};

export default useScreenUpload;
